/**
 * Browser client for PageStitch auth + project endpoints.
 *
 * Phase 6 UI. All requests use `credentials: 'include'` so the `pb_token`
 * cookie set by the Worker on /api/auth/login is sent automatically.
 */

import {
  forgetAuthSession,
  getTemplateApiOrigin,
  rememberAuthSession,
  TemplateApiError
} from './template-api.js';
import { authedFetch } from '../shared/authed-fetch.js';

async function parseJsonResponse(resp) {
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new TemplateApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return data;
}

export async function authFetch(path, init = {}) {
  const origin = getTemplateApiOrigin();
  // Auth-api posts JSON by default (FormData bodies opt out). Bearer + cookie +
  // timeout + 401 handling all live in the shared authedFetch wrapper.
  const headers = {
    ...(init.body && !(init.body instanceof FormData)
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(init.headers || {})
  };
  return authedFetch(`${origin}${path}`, { ...init, headers });
}

// ---------- auth ----------

/**
 * @param {{email:string, password:string}} creds
 * @returns {Promise<{ok:true, sub:string, email:string}>}
 */
export async function signupAccount({ email, password }) {
  const resp = await authFetch('/api/auth/signup', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
  const data = await parseJsonResponse(resp);
  rememberAuthSession(data);
  return data;
}

export async function loginAccount({ email, password }) {
  const resp = await authFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
  const data = await parseJsonResponse(resp);
  rememberAuthSession(data);
  return data;
}

export async function logoutAccount() {
  try {
    const resp = await authFetch('/api/auth/logout', { method: 'POST' });
    return await parseJsonResponse(resp);
  } finally {
    // Hard logout should be local-first even if the server is unreachable or
    // already considers the session invalid. The shell reloads immediately.
    forgetAuthSession();
  }
}

/**
 * Returns the current auth payload. Always succeeds — when no JWT is set
 * and ALLOW_LEGACY_ADMIN is on, the Worker returns `authMode: 'single-user'`.
 * When auth is enforced and the user isn't logged in, it returns 401.
 *
 * @param {{timeoutMs?:number}} [options]
 * @returns {Promise<{ok:true, sub:string, email?:string, authMode?:string}>}
 */
export async function checkAuth({ timeoutMs } = {}) {
  const resp = await authFetch('/api/auth/check', {
    method: 'GET',
    ...(Number.isFinite(timeoutMs) && timeoutMs > 0 ? { timeoutMs } : {})
  });
  try {
    const data = await parseJsonResponse(resp);
    if (isRealUser(data)) rememberAuthSession(data);
    return data;
  } catch (err) {
    if (err instanceof TemplateApiError && err.status === 401) {
      forgetAuthSession();
    }
    throw err;
  }
}

// ---------- account profile ----------

/**
 * @returns {Promise<{ok:true, sub:string, email:string, displayName:string|null,
 *   avatar:string|null, hasPassword:boolean, googleLinked:boolean,
 *   createdAt:number|null, passkeyCount:number}>}
 */
export async function getAccount() {
  const resp = await authFetch('/api/account', { method: 'GET' });
  return parseJsonResponse(resp);
}

/**
 * @param {{displayName?: string|null, avatar?: string|null}} patch — only
 *   provided keys are updated; null clears a field.
 * @returns {Promise<{ok:true, displayName:string|null, avatar:string|null}>}
 */
export async function patchAccount(patch) {
  const resp = await authFetch('/api/account', {
    method: 'PATCH',
    body: JSON.stringify(patch || {})
  });
  return parseJsonResponse(resp);
}

// ---------- web push (operator uptime alerts) ----------

/**
 * The VAPID application server key. Operator-only server-side: a non-operator
 * account gets 403 here, so this doubles as the capability probe.
 * @returns {Promise<{publicKey: string}>}
 */
export async function getPushVapidKey() {
  const resp = await authFetch('/api/push/vapid-public-key', { method: 'GET' });
  return parseJsonResponse(resp);
}

/**
 * @param {PushSubscription} subscription — passed through `toJSON()`, so the
 *   server receives exactly `{endpoint, keys:{p256dh, auth}}`. The account this
 *   is filed under comes from the session, never from this body.
 */
export async function subscribeToPush(subscription) {
  const resp = await authFetch('/api/push/subscribe', {
    method: 'POST',
    body: JSON.stringify(subscription)
  });
  return parseJsonResponse(resp);
}

export async function unsubscribeFromPush(endpoint) {
  const resp = await authFetch('/api/push/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint })
  });
  return parseJsonResponse(resp);
}

/** @returns {Promise<{sent:number, pruned:number, failed:number, total:number}>} */
export async function sendPushTest() {
  const resp = await authFetch('/api/push/test', { method: 'POST' });
  return parseJsonResponse(resp);
}

/**
 * CHANGE mode verifies currentPassword; OAuth-only accounts (hasPassword false)
 * SET a password without one.
 * @returns {Promise<{ok:true, mode:'changed'|'set'}>}
 */
export async function changePassword({ currentPassword, newPassword }) {
  const resp = await authFetch('/api/auth/change-password', {
    method: 'POST',
    // This route intentionally returns 401 for a wrong current password. Only
    // that shaped validation response is expected; auth_required must still
    // flow through the global expired-session handler.
    suppressUnauthorizedHandler: async (response) => {
      const data = await response.json().catch(() => ({}));
      return data?.field === 'currentPassword';
    },
    body: JSON.stringify({ currentPassword, newPassword })
  });
  return parseJsonResponse(resp);
}

// ---------- section favorites ----------

/**
 * @returns {Promise<{ok:true, favorites:Array<{catalogKey:string, category:string,
 *   createdAt:number}>, reason?:string}>}
 */
export async function listSectionFavorites() {
  const resp = await authFetch('/api/favorites/sections', { method: 'GET' });
  return parseJsonResponse(resp);
}

/**
 * Star or unstar one catalog section.
 * @param {string} catalogKey
 * @param {{category?: string, favorite: boolean}} opts
 * @returns {Promise<{ok:true, favorite:boolean, count:number, reason?:string}>}
 */
export async function setSectionFavorite(catalogKey, { category = '', favorite } = {}) {
  const resp = await authFetch('/api/favorites/sections', {
    method: 'POST',
    body: JSON.stringify({ catalogKey, category, favorite: Boolean(favorite) })
  });
  return parseJsonResponse(resp);
}

/**
 * Report a problem with a catalog section (MCP-5). The server derives the
 * category and validates that the key exists; `duplicate` means an open
 * report of that kind already exists for this account.
 * @param {{catalogKey:string, kind:string, message:string}} payload
 * @returns {Promise<{ok:true, id:string|null, duplicate:boolean, category?:string, reason?:string}>}
 */
export async function reportSection({ catalogKey, kind, message, screenshot = null }) {
  const resp = await authFetch('/api/sections/report', {
    method: 'POST',
    body: JSON.stringify({ catalogKey, kind, message, screenshot })
  });
  return parseJsonResponse(resp);
}

/**
 * Report a whole crawled TEMPLATE as badly ingested (TR-1). `sectionHint` is a
 * `provenancePath` and is advisory — it narrows a re-parse, it does not
 * identify the report.
 *
 * @returns {Promise<{ok:true, id:string|null, duplicate:boolean, templateDomain?:string}>}
 */
export async function reportTemplate({
  templateDomain,
  kind,
  message,
  sectionHint = null,
  attachSection = null,
  screenshot = null
}) {
  const resp = await authFetch('/api/templates/report', {
    method: 'POST',
    // `attachSection` is a catalog KEY, never bytes: the server fetches the
    // catalog original itself. A section in this project has been edited and
    // carries the client's own content, and it is the template's version that
    // is under report.
    body: JSON.stringify({
      templateDomain,
      kind,
      message,
      sectionHint,
      attachSection,
      screenshot
    })
  });
  return parseJsonResponse(resp);
}

async function uploadReportScreenshot(type, reportId, blob, { signal = null } = {}) {
  const base = type === 'template' ? '/api/templates/reports' : '/api/sections/reports';
  const encoded = encodeURIComponent(reportId);
  const put = await authFetch(`${base}/${encoded}/screen`, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': 'image/png' },
    signal,
    timeoutMs: 120_000
  });
  await parseJsonResponse(put);
  const complete = await authFetch(`${base}/${encoded}/screen/complete`, {
    method: 'POST',
    body: JSON.stringify({ part: 'screen.png' }),
    signal,
    timeoutMs: 30_000
  });
  return parseJsonResponse(complete);
}

export function uploadSectionReportScreenshot(reportId, blob, options) {
  return uploadReportScreenshot('section', reportId, blob, options);
}

export function uploadTemplateReportScreenshot(reportId, blob, options) {
  return uploadReportScreenshot('template', reportId, blob, options);
}

// ---------- diagnostic (issue) reports — IR-4 ----------

/**
 * The upload is a STATE MACHINE, not one request (see `ai/issue-report-handlers.js`).
 * Create → PUT each part → complete. These five functions are that protocol and
 * nothing else: they never assemble a bundle and never see live state. What
 * reaches them is already redacted, which is the property
 * `tests/issue-report-source-gate.js` holds.
 */

/**
 * Step 1. Declares the parts and their sizes so the server can check the daily
 * byte quota BEFORE any bytes are accepted.
 *
 * A DUPLICATE comes back `{duplicate:true, parts: []}` — no upload targets at
 * all. That is not an error and the caller must not treat it as one: the
 * occurrence count on the existing report went up, which is the whole of what a
 * second copy of a known crash is worth.
 *
 * @returns {Promise<{ok:true, id:string|null, duplicate:boolean, parts:string[]}>}
 */
export async function createIssueReport({ fingerprint, kind, message, parts = [], meta = {} }) {
  const resp = await authFetch('/api/issue-reports', {
    method: 'POST',
    body: JSON.stringify({ fingerprint, kind, message, parts, meta }),
    timeoutMs: 30_000
  });
  return parseJsonResponse(resp);
}

const ISSUE_PART_CONTENT_TYPES = {
  '.png': 'image/png',
  '.html': 'text/html',
  '.jsonl': 'application/x-ndjson'
};
function issuePartContentType(name) {
  for (const [ext, type] of Object.entries(ISSUE_PART_CONTENT_TYPES)) {
    if (String(name).endsWith(ext)) return type;
  }
  return 'application/json';
}

/**
 * Step 2, once per part. The object key is NOT supplied here and cannot be: the
 * server derives it from the authenticated sub and the row id. `name` is
 * validated against the closed `PART_NAMES` allowlist on both sides.
 */
export async function uploadIssueReportPart(reportId, name, body, { signal = null } = {}) {
  const resp = await authFetch(
    `/api/issue-reports/${encodeURIComponent(reportId)}/parts/${encodeURIComponent(name)}`,
    {
      method: 'PUT',
      body,
      headers: { 'Content-Type': issuePartContentType(name) },
      signal,
      timeoutMs: 120_000
    }
  );
  return parseJsonResponse(resp);
}

/**
 * Step 3. The server `head()`s every declared part and only then opens the
 * report. A gap leaves the row `uploading` — reapable — rather than opening a
 * report with missing evidence.
 */
export async function completeIssueReport(reportId, parts = [], { signal = null } = {}) {
  const resp = await authFetch(`/api/issue-reports/${encodeURIComponent(reportId)}/complete`, {
    method: 'POST',
    body: JSON.stringify({ parts }),
    signal,
    timeoutMs: 30_000
  });
  return parseJsonResponse(resp);
}

/**
 * Upload every part, RESUMABLY.
 *
 * Resume is possible only because each part is its own idempotent PUT: a part
 * that landed stays landed, so a retry re-sends the ones that did not rather
 * than the bundle. A part that exhausts its retries throws — and because
 * `complete` verifies with `head()`, a partial upload can never be mistaken for
 * a whole one further down the line.
 *
 * @param {string} reportId
 * @param {Array<{name:string, body:Blob|string, sha256?:string}>} parts
 * @param {{signal?:AbortSignal, retries?:number, onProgress?:(p:object)=>void}} [opts]
 * @returns {Promise<Array<{name:string, bytes:number, sha256:string|null}>>}
 */
export async function uploadIssueReportParts(
  reportId,
  parts,
  { signal = null, retries = 2, onProgress = null } = {}
) {
  const done = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    let lastErr = null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      try {
        const r = await uploadIssueReportPart(reportId, part.name, part.body, { signal });
        done.push({ name: part.name, bytes: r?.bytes ?? 0, sha256: part.sha256 ?? null });
        lastErr = null;
        break;
      } catch (err) {
        // A user cancel is not a transient failure and must not be retried.
        if (err?.name === 'AbortError' || signal?.aborted) throw err;
        // Nor is a refusal: a 4xx says this part will never be accepted, and
        // retrying it only spends the user's quota and their patience.
        if (Number(err?.status) >= 400 && Number(err?.status) < 500) throw err;
        lastErr = err;
      }
    }
    if (lastErr) throw lastErr;
    onProgress?.({ uploaded: done.length, total: parts.length, name: part.name });
  }
  return done;
}

/**
 * This account's own reports. Also the CAPABILITY PROBE: a 503
 * `issue_reports_disabled` here is how the client learns the kill switch is off
 * without a boot-time flag it has no way to read.
 *
 * @returns {Promise<{ok:true, reports:object[], reason?:string}>}
 */
export async function listIssueReports({ limit = 50, signal = null } = {}) {
  const resp = await authFetch(`/api/issue-reports?mine=1&limit=${encodeURIComponent(limit)}`, {
    method: 'GET',
    signal,
    timeoutMs: 20_000
  });
  return parseJsonResponse(resp);
}

/** Withdraw: the stored parts are deleted. The fingerprint stays, so cluster counts stay honest. */
export async function withdrawIssueReport(reportId) {
  const resp = await authFetch(`/api/issue-reports/${encodeURIComponent(reportId)}`, {
    method: 'DELETE',
    timeoutMs: 30_000
  });
  return parseJsonResponse(resp);
}

// ---------- projects ----------

/**
 * @param {{meta:object, zipBlob:Blob}} args
 * @returns {Promise<{ok:true, project:object, version:object}>}
 */
export async function saveProject({ meta, zipBlob }) {
  const form = new FormData();
  form.append('meta', JSON.stringify(meta || {}));
  form.append('bundle', zipBlob, `${meta?.projectId || 'project'}.pb.zip`);
  const resp = await authFetch('/api/projects/save', {
    method: 'POST',
    body: form,
    timeoutMs: 60_000
  });
  return parseJsonResponse(resp);
}

/**
 * @returns {Promise<{ok:true, projects:Array<{id:string,name:string,latestVersion:number,updatedAt:number}>}>}
 */
export async function listProjects() {
  const resp = await authFetch('/api/projects/list', { method: 'GET' });
  return parseJsonResponse(resp);
}

export async function getProject(projectId) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}`, { method: 'GET' });
  return parseJsonResponse(resp);
}

/**
 * Windowed, newest-first. No options → the server default window (60).
 * @param {string} projectId
 * @param {{limit?:number, before?:number, signal?:AbortSignal|null}} [opts] — `before` pages backwards
 *   (versions strictly older than it)
 * @returns {Promise<{versions:Array, hasMore:boolean, publishes:Array, latestPublish:object|null, latestExportPublish:object|null}>}
 *   `latestExportPublish` (additive) is the newest 'export-html'/'export-11ty'
 *   publish — the visual-review baseline; previews never anchor it.
 */
export async function listProjectVersions(projectId, { limit, before, signal = null } = {}) {
  const params = new URLSearchParams();
  if (Number.isInteger(limit) && limit > 0) params.set('limit', String(limit));
  if (Number.isInteger(before) && before > 0) params.set('before', String(before));
  const qs = params.toString();
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/versions${qs ? `?${qs}` : ''}`,
    { method: 'GET', signal }
  );
  return parseJsonResponse(resp);
}

// ── Pinned review comments (UX Pack F5b) — member surface ──────────────────

/** {threads, now} — pass `since` (the previous `now`) for delta polls. */
export async function listProjectComments(projectId, { since = null, signal = null } = {}) {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/comments${qs}`, {
    method: 'GET',
    signal
  });
  return parseJsonResponse(resp);
}

/** payload: {pageId, instanceId, anchorJson?, body, createdOnVersion?} */
export async function createCommentThread(projectId, payload) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/comments`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  return parseJsonResponse(resp);
}

export async function replyToCommentThread(projectId, threadId, body) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(threadId)}/replies`,
    { method: 'POST', body: JSON.stringify({ body }) }
  );
  return parseJsonResponse(resp);
}

export async function setCommentThreadStatus(projectId, threadId, status) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(threadId)}/status`,
    { method: 'POST', body: JSON.stringify({ status }) }
  );
  return parseJsonResponse(resp);
}

export async function deleteCommentThread(projectId, threadId) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(threadId)}`,
    { method: 'DELETE' }
  );
  return parseJsonResponse(resp);
}

/** EXPLICIT member re-anchor with the expected-updated-at concurrency base. */
export async function updateCommentThreadAnchor(
  projectId,
  threadId,
  anchorJson,
  expectedUpdatedAt
) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(threadId)}/anchor`,
    { method: 'POST', body: JSON.stringify({ anchorJson, expectedUpdatedAt }) }
  );
  return parseJsonResponse(resp);
}

/**
 * Owner-only. The response's `token` is shown ONCE and never retrievable again.
 *
 * SH-3c: `deliverTo` asks the server to email the link from inside this same
 * request — the only moment the raw token exists — and the response's
 * `delivered` says whether that actually happened. It is best-effort: a send
 * failure does not fail the create, and the token comes back regardless,
 * because copying it is the reliable path and the email is the convenience.
 */
export async function createReviewLink(
  projectId,
  { reviewerLabel, expiresInDays, deliverTo, access } = {}
) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/review-links`, {
    method: 'POST',
    body: JSON.stringify({
      reviewerLabel,
      ...(expiresInDays ? { expiresInDays } : {}),
      ...(deliverTo ? { deliverTo } : {}),
      // CE-6: 'content-edit' mints a ced_ client-editing link; omitted =
      // 'comment', the shape every pre-CE call still sends.
      ...(access ? { access } : {})
    })
  });
  return parseJsonResponse(resp);
}

/**
 * Owner-only. SH-3b: keeps a link the reviewer ALREADY HAS working past its
 * expiry, which is otherwise impossible — the token is stored as a hash, so the
 * alternative is minting a new one and getting it to them again.
 *
 * Extends from now, not from the current expiry. Refuses a revoked link with a
 * typed `link_revoked` (409), distinct from `link_not_found` (404).
 */
export async function extendReviewLink(projectId, linkId, { expiresInDays } = {}) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/review-links/${encodeURIComponent(linkId)}/extend`,
    { method: 'POST', body: JSON.stringify({ expiresInDays }) }
  );
  return parseJsonResponse(resp);
}

export async function listReviewLinks(projectId) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/review-links`, {
    method: 'GET'
  });
  return parseJsonResponse(resp);
}

export async function revokeReviewLink(projectId, linkId) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/review-links/${encodeURIComponent(linkId)}/revoke`,
    { method: 'POST' }
  );
  return parseJsonResponse(resp);
}

/**
 * Label an existing cloud version (1–80 chars). Owner or member. Used by
 * named checkpoints to tag the version the client is currently synced to —
 * a version is only labeled, never minted for the label's sake.
 * @param {string} projectId
 * @param {number} version
 * @param {string} label
 */
export async function labelProjectVersion(projectId, version, label) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(version)}/label`,
    { method: 'POST', body: JSON.stringify({ label }) }
  );
  return parseJsonResponse(resp);
}

/**
 * Append a row to the project's publish log (migration 006). Owner or member.
 * @param {string} projectId
 * @param {number} version — must be an existing cloud version
 * @param {{kind:'export-html'|'export-11ty'|'preview', target?:string|null}} opts
 */
export async function markProjectPublished(projectId, version, { kind, target = null } = {}) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/publish`, {
    method: 'POST',
    body: JSON.stringify({ version, kind, ...(target ? { target } : {}) })
  });
  return parseJsonResponse(resp);
}

/**
 * Record an export manifest (migration 007). Idempotent on eventId — retries
 * and multi-tab races converge on one row; `deduped: true` on replay.
 * Error codes: invalid_event_id / invalid_export_mode / invalid_lock /
 * lock_too_large (413) / lock_hash_mismatch / project_mismatch (403) /
 * event_id_conflict (409) / fleet_unavailable (503, pre-migration deploy).
 * @param {string} projectId
 * @param {{eventId:string, exportMode:'11ty'|'flat', lockText:string,
 *          artifactHash:string, projectPublishId?:string|null,
 *          projectVersion?:number|null, contentHash?:string|null}} payload
 */
export async function recordExportManifest(projectId, payload) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/exports`, {
    method: 'POST',
    body: JSON.stringify(payload),
    timeoutMs: 30_000
  });
  return parseJsonResponse(resp);
}

/**
 * Owned + member projects joined with each one's latest export manifest and
 * deploy target. Paginated on (updatedAt DESC, id) — pass `cursor` from the
 * previous response's `nextCursor`. `reason: 'fleet_unavailable'` = tables not
 * yet migrated (projects still render; fleet columns show "never recorded").
 * @param {{limit?:number, cursor?:string|null}} [opts]
 */
export async function getFleet({ limit, cursor, signal } = {}) {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const query = params.toString();
  const resp = await authFetch(`/api/fleet${query ? `?${query}` : ''}`, {
    method: 'GET',
    signal
  });
  return parseJsonResponse(resp);
}

/**
 * Export-manifest history for one project, newest first, no lock JSON.
 * `includeLock: true` requires limit === 1 and adds `lockJson` (verbatim).
 * @param {string} projectId
 * @param {{limit?:number, cursor?:string|null, includeLock?:boolean}} [opts]
 */
export async function listProjectExports(projectId, { limit, cursor, includeLock } = {}) {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  if (includeLock) params.set('include', 'lock');
  const query = params.toString();
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/exports${query ? `?${query}` : ''}`,
    { method: 'GET' }
  );
  return parseJsonResponse(resp);
}

/**
 * @returns {Promise<Blob>} The version's bundle ZIP
 */
export async function getProjectVersionBundle(projectId, version, { signal } = {}) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(version)}`,
    { method: 'GET', timeoutMs: 60_000, ...(signal ? { signal } : {}) }
  );
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new TemplateApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return await resp.blob();
}

// ---------- project thumbnails ----------

/**
 * Hand self-contained preview HTML to the Worker, which renders it via
 * Browserless and stores the PNG. Resolves 202 while the capture is still in
 * flight; 200 with `skipped:'fresh'` when the server's cooldown declined it.
 *
 * @param {string} projectId
 * @param {string} html — preview-bundler output (homepage, assets inlined)
 * @param {{force?:boolean}} [opts] — force bypasses the server freshness window
 */
export async function putProjectThumbnail(projectId, html, { force = false } = {}) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/thumb`, {
    method: 'PUT',
    body: JSON.stringify({ html, force }),
    timeoutMs: 60_000
  });
  return parseJsonResponse(resp);
}

/**
 * Fetch a stored thumbnail. Returns null when the project has no capture yet
 * (404) — callers render a monogram tile instead.
 * @returns {Promise<Blob|null>}
 */
export async function getProjectThumbnail(projectId, { signal } = {}) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/thumb`, {
    method: 'GET',
    timeoutMs: 30_000,
    signal
  });
  if (resp.status === 404) return null;
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new TemplateApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return await resp.blob();
}

// ---------- per-version page shots (visual diff) ----------

/**
 * Synchronous capture: 200 {existed:true} on an R2 cache hit (no render), 200
 * {existed:false, capturedAt} after a completed render, 503 code
 * 'shots_unavailable' when Browserless is unconfigured. Shots are write-once
 * (immutable caching); recapture = a server-side SHOT_REV bump, never a flag.
 */
export async function putProjectShot(projectId, { version, pageId, html, signal = null }) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/shots`, {
    method: 'PUT',
    body: JSON.stringify({ version, pageId, html }),
    timeoutMs: 60_000,
    ...(signal ? { signal } : {})
  });
  return parseJsonResponse(resp);
}

/**
 * @returns {Promise<Blob|null>} null when the shot is not captured yet (404).
 */
export async function getProjectShot(projectId, version, pageId, { signal } = {}) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/shots/${encodeURIComponent(version)}/${encodeURIComponent(pageId)}`,
    { method: 'GET', timeoutMs: 30_000, ...(signal ? { signal } : {}) }
  );
  if (resp.status === 404) return null;
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new TemplateApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return await resp.blob();
}

export async function deleteProject(projectId) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}`, {
    method: 'DELETE'
  });
  return parseJsonResponse(resp);
}

// ---------- project members (Collab Phase 2) ----------

/**
 * Owner or member. The owner is synthesized into the list with role 'owner'.
 * @returns {Promise<{ok:true, members:Array<{sub:string,email:string|null,
 *   displayName:string|null, role:'owner'|'editor', addedAt:number|null}>}>}
 */
export async function listProjectMembers(projectId, { signal = null } = {}) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/members`, {
    method: 'GET',
    signal
  });
  return parseJsonResponse(resp);
}

/**
 * Owner only. Adds an EXISTING account by email (no invite/email delivery).
 * Server error codes surface on TemplateApiError.data.code:
 *   user_not_found (404) · already_owner (400) · member_limit (409) ·
 *   owner_only (403). Duplicate add returns 200 {existed:true}.
 * @returns {Promise<{ok:true, existed?:boolean, member:object}>}
 */
export async function addProjectMember(projectId, email) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/members`, {
    method: 'POST',
    body: JSON.stringify({ email })
  });
  return parseJsonResponse(resp);
}

/**
 * Owner removes anyone; a member may remove only themselves ("leave").
 * @returns {Promise<{ok:true, removed:boolean}>}
 */
export async function removeProjectMember(projectId, memberSub) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberSub)}`,
    { method: 'DELETE' }
  );
  return parseJsonResponse(resp);
}

export async function listProjectInvites(projectId) {
  const resp = await authFetch(`/api/projects/${encodeURIComponent(projectId)}/invites`, {
    method: 'GET'
  });
  return parseJsonResponse(resp);
}

export async function revokeProjectInvite(projectId, inviteId) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/invites/${encodeURIComponent(inviteId)}`,
    { method: 'DELETE' }
  );
  return parseJsonResponse(resp);
}

export async function describeProjectInvite(token) {
  const resp = await authFetch(`/api/invites/${encodeURIComponent(token)}`, { method: 'GET' });
  return parseJsonResponse(resp);
}

export async function acceptProjectInvite(token) {
  const resp = await authFetch(`/api/invites/${encodeURIComponent(token)}/accept`, {
    method: 'POST'
  });
  return parseJsonResponse(resp);
}

// ---------- project assets (Collab Phase 4) ----------

/**
 * Mirror an asset blob into the project's R2 namespace. Attribution rides
 * URI-encoded X-PB-Asset-* headers; the server computes/stores its own hash.
 * Dedupe: 200 {existed:true} on identical re-PUT; 409 code 'asset_conflict'
 * on same-id/different-bytes; 413 code 'asset_too_large' over 25 MB.
 * @returns {Promise<{ok:true, existed?:boolean, asset:{id:string, hash:string}}>}
 */
export async function putProjectAsset(projectId, assetId, blob, meta = {}) {
  const headers = { 'Content-Type': meta.mime || blob?.type || 'application/octet-stream' };
  if (meta.hash) headers['X-PB-Asset-Hash'] = encodeURIComponent(meta.hash);
  if (meta.filename) headers['X-PB-Asset-Filename'] = encodeURIComponent(meta.filename);
  if (meta.source) headers['X-PB-Asset-Source'] = encodeURIComponent(meta.source);
  if (meta.author) headers['X-PB-Asset-Author'] = encodeURIComponent(meta.author);
  if (meta.authorUrl) headers['X-PB-Asset-Author-Url'] = encodeURIComponent(meta.authorUrl);
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURIComponent(assetId)}`,
    { method: 'PUT', body: blob, headers, timeoutMs: 60_000 }
  );
  return parseJsonResponse(resp);
}

/**
 * Fetch a mirrored asset blob. Returns the raw Response so the caller can
 * read the blob AND the X-PB-Asset-* attribution headers.
 * @returns {Promise<Response>}
 */
export async function getProjectAsset(projectId, assetId) {
  const resp = await authFetch(
    `/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURIComponent(assetId)}`,
    { method: 'GET', timeoutMs: 60_000 }
  );
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new TemplateApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return resp;
}

// ---------- helpers ----------

/**
 * Returns true if the auth payload represents a real user (not the dev/legacy admin fallback).
 * UI uses this to decide whether to render "Sign in" vs the user chip.
 */
export function isRealUser(authPayload) {
  if (!authPayload) return false;
  if (authPayload.authMode === 'single-user') return false;
  if (authPayload.sub === 'admin') return false;
  return Boolean(authPayload.sub);
}

// ---------- Google OAuth (popup flow) ----------

/**
 * Open a popup to the Worker's /api/auth/google/start endpoint. The Worker
 * redirects to Google, then back to /api/auth/google/callback which renders
 * an HTML page that postMessages the result to window.opener and closes.
 *
 * IMPORTANT: window.open MUST be called synchronously from a user-click
 * handler. Do not `await` anything before invoking this function — browsers
 * block popups that aren't tied directly to a user gesture.
 *
 * @returns {Promise<{ok:true, sub:string, email:string, authMode:'jwt', token:string}>}
 */
export function startGoogleOAuth() {
  const origin = getTemplateApiOrigin();
  const redirect = encodeURIComponent(window.location.origin);
  // NO `noopener` — the callback page needs window.opener to postMessage back.
  const popup = window.open(
    `${origin}/api/auth/google/start?redirect=${redirect}`,
    'pb-google-oauth',
    'width=520,height=640'
  );
  if (!popup) {
    return Promise.reject(new TemplateApiError('Popup blocked — allow popups for this site.', 0));
  }

  return new Promise((resolve, reject) => {
    let settled = false;

    const onMessage = (event) => {
      // CRITICAL: validate the message origin against the Worker origin.
      // Without this, any tab could spoof a successful auth payload.
      if (event.origin !== origin) return;
      const data = event.data;
      if (!data || data.type !== 'pb-oauth-result') return;
      cleanup();
      if (data.ok) {
        rememberAuthSession(data);
        resolve(data);
      } else {
        reject(new TemplateApiError(data.error || 'Google sign-in failed', 0));
      }
    };

    // Detect popup closed without sending a message (user cancelled).
    const interval = setInterval(() => {
      try {
        if (popup.closed && !settled) {
          cleanup();
          reject(new TemplateApiError('Cancelled', 0));
        }
      } catch {
        /* cross-origin access on popup.closed is OK in all browsers */
      }
    }, 500);

    function cleanup() {
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(interval);
    }

    window.addEventListener('message', onMessage);
  });
}
